import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import { styles } from './domain-module-viewer-styles';
import { terms } from '../../language/terms';
import { isRetiredSecurityModule } from '../../utility/security/retired-security-modules';

/** The lower-case word for each entity the viewer can describe, by its internal code. */
const entityTerm: Record<'group' | 'site' | 'user', string> = {
  group: terms.loginGroup.lower,
  site: terms.loginSite.lower,
  user: terms.loginUser.lower,
};

/**
 * Domain assignment for an entity (group, site, user)
 */
export interface DomainAssignment {
  domainCode: string;
  domainDisplayName: string;
  roleSetCode?: string;
  roleSetDisplayName?: string;
  roles?: string[];
}

/**
 * Module access derived from role assignments
 */
export interface ModuleAccess {
  moduleCode: string;
  moduleDisplayName: string;
  accessType: 'owner' | 'contributor' | 'viewer' | 'consumer';
  roles: string[];
}

/**
 * Component to display domain memberships and derived module access
 * for a group, site, or user.
 */
@customElement('domain-module-viewer')
export class DomainModuleViewer extends LitElement {
  static styles = [styles];

  /** Domain assignments for the entity */
  @property({ type: Array })
  domains: DomainAssignment[] = [];

  /** Module access derived from roles (computed from domains/roles) */
  @property({ type: Array })
  modules: ModuleAccess[] = [];

  /** Whether data is loading */
  @property({ type: Boolean })
  loading = false;

  /** Entity type for display purposes */
  @property({ type: String })
  entityType: 'group' | 'site' | 'user' = 'group';

  render() {
    if (this.loading) {
      return html`
        <div class="empty-state">
          <sl-spinner></sl-spinner>
          <p>Loading security context...</p>
        </div>
      `;
    }

    const hasDomains = this.domains.length > 0;
    const hasModules = this.shownModules.length > 0;

    // The domains listed are UserGroupDomain rows (LoginDomainScopeGrant, built from
    // UserGroupDomainScope.Domain), so they are named by the operation-type term, and the entity
    // by its own term rather than the internal `entityType` code (#398 C16c, the SA's ruling 7).
    if (!hasDomains && !hasModules) {
      return html`
        <div class="empty-state">
          <sl-icon name="shield-x"></sl-icon>
          <p>No ${terms.userGroupDomain.lowerPlural} or modules assigned.</p>
          <p style="font-size: 0.875rem;">
            Assign ${terms.userGroupDomain.lowerPlural} to this
            ${entityTerm[this.entityType]} to grant access to features.
          </p>
        </div>
      `;
    }

    return html`
      <div class="viewer-container">
        ${this.renderSummary()} ${this.renderDomains()} ${this.renderModules()}
      </div>
    `;
  }

  /**
   * The modules this viewer draws: every one it was given except a retired module's (D-175), so
   * its tag, its module count and its role count all leave it out. The grants themselves stand.
   */
  private get shownModules(): ModuleAccess[] {
    return this.modules.filter((m) => !isRetiredSecurityModule(m.moduleCode));
  }

  private renderSummary() {
    const uniqueModules = new Set(this.shownModules.map((m) => m.moduleCode));
    return html`
      <div class="summary-grid">
        <div class="summary-card">
          <div class="summary-value">${this.domains.length}</div>
          <div class="summary-label">${terms.userGroupDomain.plural}</div>
        </div>
        <div class="summary-card">
          <div class="summary-value">${uniqueModules.size}</div>
          <div class="summary-label">Modules</div>
        </div>
        <div class="summary-card">
          <div class="summary-value">
            ${this.shownModules.reduce((acc, m) => acc + m.roles.length, 0)}
          </div>
          <div class="summary-label">Roles</div>
        </div>
      </div>
    `;
  }

  private renderDomains() {
    if (this.domains.length === 0) return nothing;

    return html`
      <div class="section">
        <div class="section-header">
          <sl-icon name="diagram-3"></sl-icon>
          ${terms.userGroupDomain.plural}
        </div>
        <div class="domain-list">
          ${this.domains.map((domain) => this.renderDomainCard(domain))}
        </div>
      </div>
    `;
  }

  private renderDomainCard(domain: DomainAssignment) {
    return html`
      <div class="domain-card">
        <div class="domain-header">
          <div>
            <div class="domain-name">${domain.domainDisplayName}</div>
            <div class="domain-code">${domain.domainCode}</div>
          </div>
        </div>
        ${
          domain.roleSetDisplayName
            ? html`
                <div class="role-set-list">
                  <div class="role-set">
                    <span class="role-set-name"
                      >${domain.roleSetDisplayName}</span
                    >
                    ${
                      domain.roles && domain.roles.length > 0
                        ? html`
                            <div class="roles-inline">
                              ${domain.roles.map(
                                (role) =>
                                  html`<span class="role-tag">${role}</span>`
                              )}
                            </div>
                          `
                        : nothing
                    }
                  </div>
                </div>
              `
            : nothing
        }
      </div>
    `;
  }

  private renderModules() {
    if (this.shownModules.length === 0) return nothing;

    // Group by module
    const moduleMap = new Map<string, ModuleAccess>();
    for (const mod of this.shownModules) {
      const existing = moduleMap.get(mod.moduleCode);
      if (existing) {
        // Merge roles
        const roles = new Set([...existing.roles, ...mod.roles]);
        existing.roles = Array.from(roles);
        // Upgrade access type if needed
        existing.accessType = this.higherAccessType(
          existing.accessType,
          mod.accessType
        );
      } else {
        moduleMap.set(mod.moduleCode, { ...mod });
      }
    }

    return html`
      <div class="section">
        <div class="section-header">
          <sl-icon name="box"></sl-icon>
          Module Access
        </div>
        <div class="module-list">
          ${Array.from(moduleMap.values()).map((mod) =>
            this.renderModuleTag(mod)
          )}
        </div>
      </div>
    `;
  }

  private renderModuleTag(mod: ModuleAccess) {
    const typeClass = mod.accessType;
    const icon = this.getAccessIcon(mod.accessType);
    return html`
      <sl-tag class="module-tag ${typeClass}" size="medium">
        <sl-icon name=${icon} style="margin-right: 0.25rem;"></sl-icon>
        ${mod.moduleDisplayName}
        <span style="margin-left: 0.5rem; opacity: 0.7;"
          >(${mod.accessType})</span
        >
      </sl-tag>
    `;
  }

  private getAccessIcon(
    accessType: 'owner' | 'contributor' | 'viewer' | 'consumer'
  ): string {
    switch (accessType) {
      case 'owner':
        return 'shield-check';
      case 'contributor':
        return 'pencil-square';
      case 'viewer':
        return 'eye';
      case 'consumer':
        return 'box-arrow-in-right';
    }
  }

  private higherAccessType(
    a: 'owner' | 'contributor' | 'viewer' | 'consumer',
    b: 'owner' | 'contributor' | 'viewer' | 'consumer'
  ): 'owner' | 'contributor' | 'viewer' | 'consumer' {
    const priority = { owner: 4, contributor: 3, viewer: 2, consumer: 1 };
    return priority[a] >= priority[b] ? a : b;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'domain-module-viewer': DomainModuleViewer;
  }
}
